import React from "react";
import {
  Outlet,
  useLoaderData,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { useView } from "../ResourceViewPage";
import { Box, Button, Container, Typography } from "@mui/material";
import RenderFormNew from "../../../Components/Esaedro/RenderForm/RenderFormNew";
import { set } from "lodash";
import { Form } from "../../../Models/SysConf/Form";
import cubeApiClient from "../../../BusinessObjects/cubeApiService";
import { useQuery } from "react-query";
import { ResourceNames } from "../../../Utils/ResourceNames";
import { RootState } from "../../../BusinessObjects/Redux/app/store";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { RoutePath } from "../../../Utils/RoutePath";
import { CustomColumn } from "../../../Models/Project/CustomColumn";
import { DataGrid, Lookup } from "devextreme-react";
import { ResourceViewLink } from "../../../Models/SysConf/ResourceViewLink";
import { Column, Editing, FilterRow } from "devextreme-react/data-grid";
import notify from "devextreme/ui/notify";
import { ResourceView } from "../../../Models/SysConf/ResourceView";
import Loader from "../../../Utils/Loader";
import { Filter } from "@mui/icons-material";

type Props = {};

type ContextType = {
  objEdit: ResourceView;
  setObjEdit: React.Dispatch<React.SetStateAction<ResourceView>>;
};

export default function LinkedResources(props: Props) {
  // Redux
  const tokenApi = useSelector((state: RootState) => state.user.tokenAPI);
  const userProfile = useSelector((state: RootState) => state.user.userProfile);
  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);

  // React Router Dom
  const navigate = useNavigate();
  const { view, setView } = useView();
  const location = useLocation();

  // hooks
  const [isLoadingSaveResourceViewLink, setIsLoadingSaveResourceViewLink] =
    React.useState<boolean>(false);
  const [isNewFormVisible, setNewFormVisible] = React.useState<boolean>(false);
  const [isEditFormVisible, setEditFormVisible] =
    React.useState<boolean>(false);
  const [objEdit, setObjEdit] = React.useState<ResourceView | {}>({});
  const [initalView, setInitialView] = React.useState<ResourceView>({
    ...view,
  });

  // CHIAMATE API
  React.useEffect(() => {
    if (
      location.pathname ===
      `${RoutePath.RESOURCES_VIEWS}/${view?.View_ID}/linked_resources`
    ) {
      setEditFormVisible(false);
    }
  }, [location]);

  const {
    data: resourcesViewsLinks,
    isLoading: isLoadingResourcesViewsLinks,
    isError: isErrorResourcesViewsLinks,
    refetch: refetchResourcesViewsLinks,
    isRefetching: isRefetchingResourcesViewsLinks,
  } = useQuery(
    ["resourcesViewsLinks", view?.View_ID, tokenApi],
    () => {
      var urlToUse = `api/sys/${ResourceNames.ResourcesViewsLinks}?RecordSelection=View_ID=${view?.View_ID}`;
      const service = new cubeApiClient();

      return service.getItems(urlToUse, tokenApi);
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      retry: 0,
      refetchInterval: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchIntervalInBackground: false,
    }
  );

  const { data: form, isLoading: isLoadingForm } = useQuery(
    ["form", ResourceNames.ResourcesViewes, userProfile?.customer_ID, tokenApi],
    () => {
      const service = new cubeApiClient();

      return service
        .getItems(
          `api/sys/${ResourceNames.Resources}?RecordSelection=ResourceName='${ResourceNames.ResourcesViewes}'`,
          tokenApi
        )
        .then(
          (data: any) => {
            let urlForm = `api/sys/${ResourceNames.Forms}/query?ResourceId=${data[0].Resource_ID}`;

            return service.getItems(urlForm, tokenApi).then(
              (data: any) => {
                return data[0];
              },
              (error: any) => {
                return undefined;
              }
            );
          },
          (error: any) => {
            return undefined;
          }
        );
    },
    {
      enabled: isLoggedIn,
      refetchOnWindowFocus: false,
      retry: 0,
      refetchInterval: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchIntervalInBackground: false,
    }
  );

  const {
    data: formResourcesViewsLinks,
    isLoading: isLoadingFormResourcesViewsLinks,
  } = useQuery(
    [
      "form",
      ResourceNames.ResourcesViewsLinks,
      userProfile?.customer_ID,
      tokenApi,
    ],
    () => {
      const service = new cubeApiClient();

      return service
        .getItems(
          `api/sys/${ResourceNames.Resources}?RecordSelection=ResourceName='${ResourceNames.ResourcesViewsLinks}'`,
          tokenApi
        )
        .then(
          (data: any) => {
            let urlForm = `api/sys/${ResourceNames.Forms}/query?ResourceId=${data[0].Resource_ID}`;

            return service.getItems(urlForm, tokenApi).then(
              (data: any) => {
                return data[0];
              },
              (error: any) => {
                return undefined;
              }
            );
          },
          (error: any) => {
            return undefined;
          }
        );
    },
    {
      enabled: isLoggedIn,
      refetchOnWindowFocus: false,
      retry: 0,
      refetchInterval: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchIntervalInBackground: false,
    }
  );

  const formResourceViewLink: Form | undefined = React.useMemo(() => {
    if (formResourcesViewsLinks) {
      let tmpForm: Form = formResourcesViewsLinks;
      tmpForm.FormsGroups.forEach((fg) => {
        fg.FormsFields.forEach((ff) => {
          if (ff.FieldName === "View_ID") {
            ff.Is_Editable = false;
          }
        });
      });

      return tmpForm;
    }

    return undefined;
  }, [formResourcesViewsLinks]);

  // Chiamata per eliminare una risorsa collegata
  const deleteResourceViewLink = (e: ResourceViewLink) => {
    const service = new cubeApiClient();

    service
      .deleteObject(e, `api/sys/${ResourceNames.ResourcesViewsLinks}`, tokenApi)
      .then(
        (data) => {
          refetchResourcesViewsLinks();
          notify("Risorsa collegata eliminata con successo", "success", 1000);
        },
        (error) => {
          refetchResourcesViewsLinks();
          notify(
            "Errore nell'eliminazione della risorsa collegata",
            "error",
            1000
          );
        }
      );
  };

  // Chiamata per salvare una nuova risorsa collegata
  const saveNewResourceViewLink = () => {
    const service = new cubeApiClient();

    setIsLoadingSaveResourceViewLink(true);

    service
      .updateObject(
        view,
        `api/sys/${ResourceNames.ResourcesViewsLinks}`,
        tokenApi
      )
      .then(
        (data) => {
          setNewFormVisible(false);
          refetchResourcesViewsLinks();
          notify("Risorsa collegata salvata con successo", "success", 1000);
        },
        (error) => {
          refetchResourcesViewsLinks();
          notify(
            "Errore nel salvataggio della risorsa collegata",
            "error",
            1000
          );
        }
      );
  };

  const columnsDataGridResourcesViewLinks: CustomColumn[] = [
    {
      caption: "Risorsa Collegata",
      dataField: "ResourceName",
    },
    {
      caption: "Join",
      dataField: "JoinType_Descr",
    },
  ];

  // ! Inizio del DOM
  return (
    <>
      <Typography
        variant="h6"
        color={"primary.main"}
        style={{ fontWeight: 600, textAlign: "center" }}
        className="pt-3"
      >
        RISORSE COLLEGATE
      </Typography>
      {view === undefined ||
      view === null ||
      resourcesViewsLinks === undefined ? (
        <Loader />
      ) : (
        <Container maxWidth="lg" sx={{ mt: 5 }}>
          {/* Bottone aggiungi, visulizzo il form e devisualizzo la DataGrid */}
          {!isEditFormVisible && !isNewFormVisible && (
            <Box sx={{ width: "100%", mb: 3, textAlign: "right" }}>
              <Button
                variant="contained"
                onClick={() => {
                  setNewFormVisible(true);
                  setView(initalView);
                }}
              >
                Aggiungi
              </Button>
            </Box>
          )}

          {/* Box con Data Grid al mounted o eventuale form di aggiunta */}
          <Box sx={{ width: "100%", height: "100%", mb: 2 }}>
            {!isNewFormVisible ? (
              <>
                {!isEditFormVisible ? (
                  <DataGrid
                    className="mb-5"
                    dataSource={resourcesViewsLinks || []}
                    showBorders={true}
                    showRowLines={true}
                    showColumnLines={true}
                    onRowRemoved={(e) => {
                      deleteResourceViewLink(e.data as ResourceViewLink);
                    }}
                    onEditingStart={(e) => {
                      navigate("edit");
                      setObjEdit(e.data);
                      setEditFormVisible(true);
                    }}
                  >
                    <FilterRow visible={true} />
                    {columnsDataGridResourcesViewLinks.map(
                      (column: CustomColumn, i: number) => (
                        <Column
                          key={i}
                          dataField={column.dataField}
                          caption={column.caption}
                        />
                      )
                    )}
                    <Editing
                      allowUpdating={true}
                      allowDeleting={true}
                      confirmDelete={true}
                      useIcons={true}
                      startEditAction="click"
                    />
                  </DataGrid>
                ) : (
                  <Outlet context={{ objEdit, setObjEdit }} />
                )}
              </>
            ) : (
              <RenderFormNew
                showTitle={false}
                isNew={true}
                myObj={view}
                form={formResourceViewLink}
                saveNewObject={(e) => setView(e)}
              />
            )}

            {/* Box con i bottoni di annulla e salva */}
            {isNewFormVisible ? (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  mt: 1,
                  flexDirection: "row",
                }}
              >
                {/* Bottone di Anulla, mi riporta alla DataGrid */}
                <Button
                  variant="contained"
                  color="error"
                  className="me-2"
                  onClick={() => setNewFormVisible(false)}
                >
                  Annulla
                </Button>

                {/* Bottone salva */}
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => saveNewResourceViewLink()}
                >
                  Salva
                </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  mt: 1,
                  flexDirection: "row",
                }}
              >
                {/* Bottone di Anulla, mi riporta al form iniziale */}
                {!isNewFormVisible && !isEditFormVisible && (
                  <>
                    <NavLink
                      to={`${RoutePath.RESOURCES_VIEWS}/${view.View_ID}`}
                    >
                      <Button
                        variant="contained"
                        color="error"
                        className="me-2"
                        onClick={() => setNewFormVisible(false)}
                      >
                        Torna Indietro
                      </Button>
                    </NavLink>

                    {/* Bottone salva, mi riporta alla home*/}
                    <NavLink to={RoutePath.RESOURCES_VIEWS}>
                      <Button variant="contained" color="success">
                        Torna alla griglia
                      </Button>
                    </NavLink>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Container>
      )}
    </>
  );
}

export function useObjEdit() {
  return useOutletContext<ContextType>();
}
